import { forwardRef, type HTMLAttributes } from 'react';

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /** バッジのバリアント */
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info';
  /** バッジのサイズ */
  size?: 'sm' | 'md';
}

/** @graph-connects none */
const variantStyles = {
  default:
    'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] border border-[var(--color-border)]',
  success:
    'bg-[var(--color-success-muted)] text-[var(--color-success)] border border-[var(--color-success)]/20',
  warning:
    'bg-[var(--color-warning-muted)] text-[var(--color-warning)] border border-[var(--color-warning)]/20',
  error:
    'bg-[var(--color-error-muted)] text-[var(--color-error)] border border-[var(--color-error)]/20',
  info: 'bg-[var(--color-info-muted)] text-[var(--color-info)] border border-[var(--color-info)]/20',
};

/** @graph-connects none */
const sizeStyles = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
};

/**
 * @graph-stack cortex-admin
 * @graph-domain Engineering
 * @graph-business バッジコンポーネント。5種のバリアント（default/success/warning/error/info）と2段階サイズのピル型ラベル
 * @graph-connects none
 */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ variant = 'default', size = 'sm', className = '', children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={`
          inline-flex items-center justify-center
          font-medium rounded-full
          ${variantStyles[variant]}
          ${sizeStyles[size]}
          ${className}
        `}
        {...props}
      >
        {children}
      </span>
    );
  },
);

Badge.displayName = 'Badge';
